import { Badge, Box, Button, Flex, HStack, Input, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { ResistomapSelect } from '@resistapp/client/components/forms/resistomap-select';
import { theme } from '@resistapp/client/components/shared/theme';
import { Loading } from '@resistapp/client/components/states/loading';
import { useAdminChips, useProjects } from '@resistapp/client/hooks/api';
import { AdminChipRow } from '@resistapp/common/api-types';
import type { GroupBase } from 'chakra-react-select';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

type Option = { value: string | number; label: string };

const CHIP_NAME_PATTERN = /^\d{6}[A-Za-z]?$/;

const normaliseChipName = (value: string): string => {
  const trimmed = value.trim();
  const upper = trimmed.toUpperCase();
  if (trimmed && CHIP_NAME_PATTERN.test(upper)) {
    return upper;
  }
  return trimmed;
};

export function AdminChipsView() {
  const { data: projects } = useProjects();

  const [projectOpt, setProjectOpt] = useState<Option | null>(null);
  const [chipNameFilter, setChipNameFilter] = useState('');
  const [chipNameInput, setChipNameInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<Option>({ value: 100, label: '100' });

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (projectOpt) p.set('projectId', String(projectOpt.value));
    if (chipNameFilter.trim()) p.set('chipName', chipNameFilter.trim());
    p.set('page', String(page));
    p.set('pageSize', String(pageSize.value));
    return p;
  }, [projectOpt, chipNameFilter, page, pageSize]);

  const { data, isPending, error } = useAdminChips(qs);

  const commitChipNameFilter = useCallback(
    (rawValue: string) => {
      const normalised = normaliseChipName(rawValue);
      if (normalised !== chipNameFilter) {
        setChipNameFilter(normalised);
        setPage(1);
      }
      setChipNameInput(normalised);
    },
    [chipNameFilter, setPage],
  );

  const projectOptions = useMemo<Option[]>(
    () =>
      (projects || [])
        .slice()
        .sort((a, b) => {
          const tb = new Date(b.createdAt).getTime();
          const ta = new Date(a.createdAt).getTime();
          const bHas = Number.isFinite(tb);
          const aHas = Number.isFinite(ta);
          if (aHas && bHas) return tb - ta;
          if (aHas && !bHas) return -1;
          if (!aHas && bHas) return 1;
          return b.id - a.id;
        })
        .map(p => ({ value: p.id, label: p.name })),
    [projects],
  );
  const pageSizeOptions: Option[] = useMemo(() => [20, 50, 100].map(n => ({ value: n, label: String(n) })), []);

  if (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
  if (isPending) {
    return (
      <div style={{ marginBottom: theme.spacing[16] }}>
        <TitleBar title="Chips" $extraWide={true} />
        <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
          <Box padding={2} borderRadius={6} bg="white" boxShadow="sm">
            <Loading />
          </Box>
        </VStack>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title="Chips" $extraWide={true} />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <SimpleGrid columns={{ base: 1, md: 3, lg: 4 }} spacing={theme.spacing[3]}>
          <ResistomapSelect<Option, false, GroupBase<Option>>
            options={projectOptions}
            value={projectOpt}
            onChange={opt => {
              setProjectOpt(opt);
            }}
            placeholder="Project"
            isClearable
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
          />
          <Input
            placeholder="Chip name"
            value={chipNameInput}
            onChange={e => {
              const nextValue = e.target.value;
              setChipNameInput(nextValue);
              const trimmed = nextValue.trim();
              if (trimmed && CHIP_NAME_PATTERN.test(trimmed.toUpperCase())) {
                commitChipNameFilter(nextValue);
              }
            }}
            onBlur={event => {
              commitChipNameFilter(event.currentTarget.value);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitChipNameFilter(event.currentTarget.value);
              }
              if (event.key === 'Escape') {
                setChipNameInput(chipNameFilter);
              }
            }}
            bg="white"
          />
        </SimpleGrid>

        <Box>
          {data.rows.length === 0 ? (
            <Text>No chips match your filters.</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {data.rows.map((row: AdminChipRow) => (
                <Box key={row.chipId} padding={2} borderRadius={6} bg="white" boxShadow="sm">
                  <Flex justify="space-between" align="center">
                    <Box>
                      <HStack spacing={3} align="baseline">
                        <HStack
                          as={Link}
                          to={`/chip/${row.chipId}`}
                          spacing={3}
                          align="baseline"
                          style={{ textDecoration: 'none' }}
                        >
                          <Text color="gray.600">{row.chipId}</Text>
                          <Text fontWeight="bold">{row.name}</Text>
                        </HStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        {formatRunDate(row.runDate)}
                        {formatChipConfiguration(row) ? ` · ${formatChipConfiguration(row)}` : ''}
                      </Text>
                      <HStack spacing={2} mt={1} wrap="wrap">
                        {row.projects.map(p => (
                          <Badge as={Link} to={`/research/${p.id}`} key={p.id} colorScheme="green">
                            {p.name}
                          </Badge>
                        ))}
                      </HStack>
                    </Box>
                  </Flex>
                </Box>
              ))}
              <Flex justify="space-between" align="center" mt={2}>
                <Text fontSize="sm">Total: {data.total}</Text>
                <HStack>
                  <ResistomapSelect<Option, false, GroupBase<Option>>
                    options={pageSizeOptions}
                    value={pageSize}
                    onChange={opt => {
                      if (opt) setPageSize(opt);
                    }}
                    placeholder="Page size"
                    chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white', width: '160px' }) }}
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      setPage(p => Math.max(1, p - 1));
                    }}
                    isDisabled={page <= 1}
                  >
                    Prev
                  </Button>
                  <Text>Page {page}</Text>
                  <Button
                    size="sm"
                    onClick={() => {
                      setPage(p => p + 1);
                    }}
                    isDisabled={data.rows.length < Number(pageSize.value)}
                  >
                    Next
                  </Button>
                </HStack>
              </Flex>
            </VStack>
          )}
        </Box>
      </VStack>
    </div>
  );
}

function formatChipConfiguration(row: AdminChipRow): string {
  const parts: string[] = [];
  if (row.dispenser != null) {
    const prefix = row.dispenserPrefix ?? 'MSND';
    parts.push(`${prefix}${row.dispenser}`);
  }
  if (row.cycler != null) parts.push(`C${row.cycler}`);
  if (row.mmLot) parts.push(`MM Lot: ${row.mmLot}`);
  if (row.waterLot) parts.push(`qPCR Water Lot: ${row.waterLot}`);
  if (row.glycerolLot) parts.push(`Glycerol Lot: ${row.glycerolLot}`);
  if (row.antifoamBLot) parts.push(`Antifoam B Lot: ${row.antifoamBLot}`);
  if (row.kitLot) parts.push(`Kit Lot: ${row.kitLot}`);
  return parts.length ? parts.join(' · ') : '-';
}

function formatRunDate(runDate: string | null): string {
  if (!runDate) return '-';
  try {
    return new Date(runDate).toISOString().slice(0, 10);
  } catch {
    return '-';
  }
}
